import { ReducerCreators, PayloadAction } from '@reduxjs/toolkit';

import { AccessState, SerializedUserAccess } from './types';
import { GetChannelAccess } from '@/connection/access/get-channel-access';
import { GrantAccess } from '@/connection/access/grant-access';
import { RevokeAccess } from '@/connection/access/revoke-access';
import { ReplaceUserAccess } from '@/connection/access/replace-access';
import { requestDataToUserAccess } from './utils';
import { EPermissions } from '@/types/permissions.types';
import { Access } from '@/access/access';
import { serializeError } from '@/store/utils/serialize-error';

export const reducers = (create: ReducerCreators<AccessState>) => ({
  getUsers: create.asyncThunk(
    async (
      {
        channel,
        page,
        limit,
        search,
        roleFilter,
      }: { channel: number; page: number; limit?: number; search?: string; roleFilter?: EPermissions | '' },
      { rejectWithValue }
    ) => {
      try {
        const access = await GetChannelAccess(channel, page, limit, search, roleFilter);
        return access;
      } catch (error) {
        return rejectWithValue(serializeError(error));
      }
    },
    {
      pending: (state) => {
        state.gettingUsers = true;
        state.errorGettingUsers = undefined;
      },
      rejected: (state) => {
        state.errorGettingUsers = 'Error getting users';
      },
      fulfilled: (state, action) => {
        state.users = (action.payload?.data ?? []).map(requestDataToUserAccess);
        state.page = 1;
        state.totalUsers = action.payload?.total ?? 0;
      },

      settled: (state) => {
        state.gettingUsers = false;
      },
    }
  ),
  getMoreUsers: create.asyncThunk(
    async (
      {
        channel,
        page,
        limit,
        search,
        roleFilter,
      }: { channel: number; page: number; limit?: number; search?: string; roleFilter?: EPermissions | '' },
      { rejectWithValue }
    ) => {
      try {
        const access = await GetChannelAccess(channel, page, limit, search, roleFilter);
        return access;
      } catch (error) {
        return rejectWithValue(serializeError(error));
      }
    },
    {
      pending: (state) => {
        state.gettingUsers = true;
        state.errorGettingUsers = undefined;
      },
      rejected: (state) => {
        state.errorGettingUsers = 'Error getting users';
      },
      fulfilled: (state, action) => {
        state.users = (action.payload?.data ?? []).map(requestDataToUserAccess);
        state.page = state.page + 1;
        state.totalUsers = action.payload?.total ?? 0;
      },

      settled: (state) => {
        state.gettingUsers = false;
      },
    }
  ),
  // Fetches the currently logged-in user's own resource-level access
  getChannelProjectsAccess: create.asyncThunk(
    async ({ channel, email }: { channel: number; email: string }, { rejectWithValue }) => {
      try {
        // limit is generous (not 1) because the backend search is a fuzzy text match, not an
        // exact email lookup, and isn't guaranteed to rank the caller's own record first
        const access = await GetChannelAccess(channel, 1, 50, email);
        return access;
      } catch (error) {
        return rejectWithValue(serializeError(error));
      }
    },
    {
      fulfilled: (state, action) => {
        const match = (action.payload?.data ?? []).find((user) => user.email === action.meta.arg.email);
        if (match) {
          state.viewerAccess = requestDataToUserAccess(match);
        }
      },
    }
  ),
  grantUserAccess: create.asyncThunk(
    async (
      { channel, email, accesses }: { channel: number; email: string; accesses: Access[] },
      { rejectWithValue }
    ) => {
      try {
        const access = await GrantAccess(
          channel,
          email,
          accesses.map((access) => access.toResourceRequest())
        );
        return access;
      } catch (error) {
        return rejectWithValue(serializeError(error));
      }
    },
    {
      pending: (state, action) => {
        state.updatingUser = action.meta?.arg.email ?? '';
        state.errorGettingUsers = undefined;
      },
      rejected: (state) => {
        state.errorGettingUsers = 'Error getting users';
      },
      fulfilled: (state, action) => {
        state.users.forEach((user) => {
          if (user.userId === action.meta?.arg.email) {
            user.permissions = [...user.permissions, ...action.meta.arg.accesses.map((a) => a.toJSON())];
          }
        });
      },
      settled: (state) => {
        state.updatingUser = '';
      },
    }
  ),
  revokeAccess: create.asyncThunk(
    async ({ channel, email }: { channel: number; email: string }, { rejectWithValue }) => {
      try {
        const access = await RevokeAccess(channel, email);
        return access;
      } catch (error) {
        return rejectWithValue(serializeError(error));
      }
    },
    {
      pending: (state, action) => {
        state.updatingUser = action.meta?.arg.email ?? '';
        state.errorGettingUsers = undefined;
      },
      rejected: (state) => {
        state.errorGettingUsers = 'Error getting user';
      },
      fulfilled: (state, action) => {
        state.users.forEach((user) => {
          if (user.userId === action.meta?.arg.email) {
            user.permissions = [];
          }
        });
      },
      settled: (state) => {
        state.updatingUser = '';
      },
    }
  ),
  replaceAccess: create.asyncThunk(
    async (
      {
        channel,
        email,
        accesses,
        roles,
      }: { channel: number; email: string; accesses: ReturnType<Access['toJSON']>[]; roles: EPermissions[] },
      { rejectWithValue }
    ) => {
      try {
        // Deserialize to Access instances to call .toString()
        const accessInstances = accesses.map((a) => Access.fromJSON(a));
        const access = await ReplaceUserAccess(
          channel,
          email,
          accessInstances.map((access) => access.toString()),
          roles
        );
        return access;
      } catch (error) {
        return rejectWithValue(serializeError(error));
      }
    },
    {
      pending: (state, action) => {
        state.updatingUser = action.meta?.arg.email ?? '';
        state.errorGettingUsers = undefined;
      },
      rejected: (state) => {
        state.errorGettingUsers = 'Error getting user';
      },
      fulfilled: (state, action) => {
        state.users.forEach((access) => {
          if (access.userId === action.meta.arg.email) {
            access.permissions = action.meta.arg.accesses; // Already serialized
            access.user.roles = action.meta.arg.roles;
            access.roles = action.meta.arg.roles;
          }
        });
      },
      settled: (state) => {
        state.updatingUser = '';
      },
    }
  ),
  updateAccess: create.reducer((state, action: PayloadAction<SerializedUserAccess>) => {
    const userIndex = state.users.findIndex((user) => user.userId === action.payload.userId);
    if (userIndex !== -1) {
      state.users[userIndex] = action.payload;
    }
  }),
});
